import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Alert, KeyboardAvoidingView, Platform } from 'react-native'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigation, useRoute } from '@react-navigation/native';
import PostCard from '../../Components/PostCard'
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import { theme } from '../../constants/theme';
import {Send} from '../../../assets/icons';
import Loading from '../../Components/Loading';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import { createComment, fetchPostDetails, removeComment, removePost } from '../../services/postService';
import { Supabase } from '../../Utils/supabase.config';
import CommentItem from '../../Components/CommentItem';
import ReplyInput from '../../Components/ReplyInput';
import { getUserData } from '../../services/userService';
import Button from '../../Components/Button';
import Input from '../../Components/Input';
import { createNotification } from '../../services/notificationService';
import { getUserCommunityRole } from '../../services/communityService';
import { 
  createComment as createCommentService, 
  createReply, 
  fetchPostComments, 
  deleteComment,
  subscribeToComments,
  subscribeToReplies 
} from '../../services/commentService';

// Define types
interface RouteParams {
  postId: string | number;
  commentId?: string;
}

interface Comment {
  id: string;
  text: string; // Primary field name in database
  body?: string; // Keep for backward compatibility
  userId: string;
  postId: number;
  parent_id?: string;
  created_at: string;
  user?: {
    id: string;
    name: string;
    image?: string;
  };
  replies?: Comment[];
}

interface Post {
  id: number;  // Changed from string to number since database uses bigint
  body?: string;
  file?: string | string[];
  created_at: string;
  userId: string;
  is_deal?: boolean;
  user?: {
    id: string;
    name: string;
    image?: string;
  };
  comments: Comment[];
  postLikes?: any[];
  community_id?: string; // Added for community membership check
}

const PostDetails = () => {
    const route = useRoute();
    const navigation = useNavigation<any>();
    const { postId, commentId } = route.params as RouteParams;

    const [post, setPost] = useState<Post | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [likes, setLikes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [replyLoading, setReplyLoading] = useState(false);
    const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
    const { user, userData } = useAuth();
    const commentRef = useRef("");
    const inputRef = useRef<TextInput>(null);
    const [startLoading, setStartLoading] = useState(true);

    const handleNewComment = async (payload: any) => {
        if (payload.new) {
            const newComment = payload.new as Comment;
            
            // If it's a reply, add it to the parent comment's replies
            if (newComment.parent_id) {
                setComments(prev => prev.map(comment => {
                    if (comment.id === newComment.parent_id) {
                        return {
                            ...comment,
                            replies: [...(comment.replies || []), newComment]
                        };
                    }
                    return comment;
                }));
            } else {
                // It's a top-level comment
                setComments(prev => {
                    const exists = prev.some(c => c.id === newComment.id);
                    if (exists) {
                        return prev;
                    }
                    return [...prev, { ...newComment, replies: [] }];
                });
            }
        }
    };

    const handleDeleteComment = (payload: any) => {
        if (payload.old) {
            const deletedCommentId = payload.old.id;
            const deletedComment = payload.old as Comment;
            
            if (deletedComment.parent_id) {
                // It's a reply, remove it from parent's replies
                setComments(prev => prev.map(comment => {
                    if (comment.id === deletedComment.parent_id) {
                        return {
                            ...comment,
                            replies: (comment.replies || []).filter(reply => reply.id !== deletedCommentId)
                        };
                    }
                    return comment;
                }));
            } else {
                // It's a top-level comment
                setComments(prev => prev.filter(comment => comment.id !== deletedCommentId));
            }
        }
    };

    const handleLikesEvent = (payload: any) => {
        if (payload.eventType === 'INSERT') {
            setLikes(prev => [...prev, payload.new]);
        } else if (payload.eventType === 'DELETE') {
            setLikes(prev => prev.filter(like => like.id !== payload.old.id));
        }
    };

    useEffect(() => {
        if (!postId) {
            console.warn('PostDetails: No postId received in route params:', route.params);
            setStartLoading(false);
            setPost(null);
            return;
        }
        
        // Ensure postId is a number (database uses bigint)
        const numericPostId = typeof postId === 'string' ? parseInt(postId, 10) : postId;
        if (isNaN(numericPostId)) {
            console.warn('PostDetails: Invalid postId format:', postId);
            setStartLoading(false);
            setPost(null);
            return;
        }
        
        getPostDetails();
        getComments();

        // Subscribe to comment changes using the new service
        const commentsChannel = subscribeToComments(numericPostId, handleNewComment);
        const deleteChannel = subscribeToComments(numericPostId, handleDeleteComment);

        // Subscribe to likes changes
        let likesChannel = Supabase
        .channel('post-likes')
        .on('postgres_changes' as any, { 
            event: '*', 
            schema: 'public', 
            table: 'post_likes',
            filter: `postId=eq.${numericPostId}`,
        } as any, handleLikesEvent)
        .subscribe();

        return () => {
            Supabase.removeChannel(commentsChannel);
            Supabase.removeChannel(deleteChannel);
            Supabase.removeChannel(likesChannel);
        }
    }, [postId]);

    const getPostDetails = async () => {
        try {
            // Ensure postId is a number
            const numericPostId = typeof postId === 'string' ? parseInt(postId, 10) : postId;
            if (isNaN(numericPostId)) {
                console.error('PostDetails: Cannot fetch - invalid postId:', postId);
                setStartLoading(false);
                setPost(null);
                return;
            }
            
            let res = await fetchPostDetails(numericPostId);
            setStartLoading(false);
            if (res.success && res.data) {
                const postData = res.data as Post;
                setPost(postData);
                setLikes(postData.postLikes || []);
                // Fallback: if detailed post payload already includes comments, hydrate local state
                if (Array.isArray((postData as any).comments) && (postData as any).comments.length > 0) {
                    // Normalize to ensure "replies" key exists for UI
                    const normalized = (postData as any).comments.map((c: any) => ({
                        ...c,
                        body: c.body ?? c.text,
                        replies: Array.isArray(c.replies) ? c.replies : []
                    }));
                    setComments(normalized);
                }
            } else {
                console.warn('PostDetails: Post not found or fetch failed:', res.msg);
                setPost(null);
            }
        } catch (error) {
            console.error('PostDetails: Error fetching post details:', error);
            setStartLoading(false);
            setPost(null);
        }
    }

    const getComments = async () => {
        try {
            // Ensure postId is a number
            const numericPostId = typeof postId === 'string' ? parseInt(postId, 10) : postId;
            if (isNaN(numericPostId)) {
                console.error('PostDetails: Cannot fetch comments - invalid postId:', postId);
                return;
            }
            
            const res = await fetchPostComments(numericPostId);
            if (res.success && res.data) {
                setComments(res.data);
            }
        } catch (error) {
            console.error('PostDetails: Error fetching comments:', error);
        }
    }

    const onNewComment = async () => {
        if (!commentRef.current || !post) return null;
        let data = {
            userId: user?.id,
            postId: post?.id,
            text: commentRef.current, // Use 'text' field to match existing DB schema
        };

        setLoading(true);
        let res = await createCommentService(data);
        setLoading(false);
        
        if (res.success && res.data) {
            // Add the new comment to local state immediately
            const newComment: Comment = {
                id: res.data.id,
                text: commentRef.current,
                userId: user?.id || '',
                postId: post?.id || 0,
                created_at: new Date().toISOString(),
                user: {
                    id: user?.id || '',
                    name: userData?.name || 'User',
                    image: userData?.image
                },
                replies: []
            };
            
            setComments(prev => {
                const exists = prev.some(c => c.id === newComment.id);
                if (exists) {
                    return prev;
                }
                return [...prev, newComment];
            });
            
            if (user?.id && user.id !== post.userId) {
                // send notification
                let notify = {
                    senderId: user.id,
                    receiverId: post.userId,
                    title: 'commented on your post',
                    data: JSON.stringify({postId: post.id, commentId: res.data.id})
                }
                createNotification(notify);
            }

            inputRef?.current?.clear();
            commentRef.current = "";
        } else {
            console.error('Comment creation failed:', res);
            Alert.alert('Comment', (res as any).msg || 'Something went wrong');
        }
    }

    const onReply = (comment: Comment) => {
        setReplyingTo(comment);
    }

    const onSendReply = async (replyText: string) => {
        if (!replyingTo || !post) return;
        
        setReplyLoading(true);
        const replyData = {
            userId: user?.id,
            postId: post.id,
            text: replyText, // Use 'text' field to match existing DB schema
            parentId: replyingTo.id
        };

        const res = await createReply(replyData);
        setReplyLoading(false);
        
        if (res.success && res.data) {
            const newReply: Comment = {
                id: res.data.id,
                text: replyText,
                userId: user?.id || '',
                postId: post.id,
                parent_id: replyingTo.id,
                created_at: new Date().toISOString(),
                user: {
                    id: user?.id || '',
                    name: userData?.name || 'User',
                    image: userData?.image
                }
            };

            // Add reply to the parent comment
            setComments(prev => prev.map(comment => {
                if (comment.id === replyingTo.id) {
                    return {
                        ...comment,
                        replies: [...(comment.replies || []), newReply]
                    };
                }
                return comment;
            }));

            // Send notification to comment author
            if (user?.id && user.id !== replyingTo.userId) {
                const notify = {
                    senderId: user.id,
                    receiverId: replyingTo.userId,
                    title: 'replied to your comment',
                    data: JSON.stringify({postId: post.id, commentId: replyingTo.id, replyId: res.data.id})
                };
                createNotification(notify);
            }

            setReplyingTo(null);
        } else {
            Alert.alert('Reply', (res as any).msg || 'Something went wrong');
        }
    }

    const onCancelReply = () => {
        setReplyingTo(null);
    }

    const onDeleteComment = async (comment: Comment) => {
        let res = await deleteComment(comment.id);
        if (res.success) {
            if (comment.parent_id) {
                // It's a reply, remove from parent's replies
                setComments(prev => prev.map(parentComment => {
                    if (parentComment.id === comment.parent_id) {
                        return {
                            ...parentComment,
                            replies: (parentComment.replies || []).filter(reply => reply.id !== comment.id)
                        };
                    }
                    return parentComment;
                }));
            } else {
                // It's a top-level comment
                setComments(prev => prev.filter(c => c.id !== comment.id));
            }
        } else {
            Alert.alert('Comment', (res as any).msg || 'Something went wrong');
        }
    }

    const onDeletePost = async () => {
        if (!post) return;
        let res = await removePost(post.id);
        if (res.success) {
            navigation.goBack();
        } else {
            Alert.alert('Post', (res as any).msg || 'Something went wrong');
        }
    }

    const onEditPost = async () => {
        if (!post) return;
        
        // Check if user is a member of the community (if the post is in a community)
        if (post.community_id) {
            const membershipResult = await getUserCommunityRole(user?.id, post.community_id);
            if (!membershipResult.success || !membershipResult.data) {
                Alert.alert('Permission Denied', 'You must be a member of this community to edit posts.');
                return;
            }
        }
        
        navigation.goBack();
        navigation.navigate('newPost', {...post});
    }

    if (startLoading) {
        return (
            <ScreenWrapper bg="white">
                <Header title="Post Details" />
                <View style={styles.center}>
                    <Loading />
                </View>
            </ScreenWrapper>
        )
    }

    if (!post) {
        return (
            <ScreenWrapper bg="white">
                <Header title="Post Details" />
                <View style={[styles.center, {justifyContent: 'flex-start', marginTop: 100}]}>
                    <Text style={styles.notFound}>Post not found !</Text>
                </View>
            </ScreenWrapper>     
        )
    }
    
  return (
    <ScreenWrapper bg="white">
        <Header title="Post Details" />
        <KeyboardAvoidingView 
            style={styles.container}
            behavior={Platform.OS === 'ios' ? 'padding' : Platform.OS === 'web' ? undefined : 'height'}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : Platform.OS === 'web' ? 0 : 20}
        >
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.list}>
            {post && (
                <PostCard
                    item={{
                        ...post, 
                        comments: post.comments ? [{count: post.comments.length}] : []
                    }} 
                    currentUser={user}
                    showMoreIcon={false}
                    hasShadow={false}
                    showDelete={true}
                    onDelete={onDeletePost}
                    onEdit={onEditPost}
                    // Post details is not a high-volume list, and links here are less critical;
                    // keep default behavior (allowLinks=false) to avoid extra RPC here.
                />
            )}

            {/* comment input */}
            <View style={styles.inputContainer}>
                <Input
                    ref={inputRef}
                    placeholder='Type comment...'
                    placeholderTextColor={theme.colors.textLight}
                    onChangeText={value => commentRef.current = value}
                    containerStyle={{flex: 1, height: hp(6.2), borderRadius: theme.radius.xl}}
                />

                {
                    loading ? (
                        <View style={styles.loading}>
                            <Loading size="small" />
                        </View>
                    ) : (
                        <TouchableOpacity onPress={onNewComment} style={styles.sendIcon}>
                            <Send color={theme.colors.primaryDark} />
                        </TouchableOpacity>
                    )
                }
            </View>

            {/* Reply Input */}
            {replyingTo && (
                <ReplyInput
                    onSendReply={onSendReply}
                    onCancel={onCancelReply}
                    replyingTo={replyingTo}
                    loading={replyLoading}
                />
            )}

            {/* comment list */}
            <View style={{marginVertical: 15, gap: 17}}>
                {
                    comments?.map(comment => 
                        <CommentItem 
                            item={comment} 
                            canDelete={user?.id == comment.userId || user?.id == post?.userId}
                            onDelete={() => onDeleteComment(comment)}
                            onReply={onReply}
                            replies={comment.replies || []}
                            key={comment.id.toString()} 
                            highlight={comment.id == commentId}
                        />
                    )
                }
                {
                    comments?.length == 0 && (
                        <Text style={{color: theme.colors.text, marginLeft: 5,}}>Be first to comment!</Text>
                    )
                }
            </View>
        </ScrollView>
        </KeyboardAvoidingView>
    </ScreenWrapper>
  )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'white',
        paddingVertical: Platform.OS === 'web' ? wp(2) : wp(7),
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10
    },
    list: {
        paddingHorizontal: wp(4),
    },
    sendIcon: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 0.8,
        borderColor: theme.colors.primary,
        borderRadius: theme.radius.lg,
        height: hp(5.8),
        width: hp(5.8)
    },
    center: {
        flex: 1, 
        alignItems: 'center', 
        justifyContent: 'center'
    },
    notFound: {
        fontSize: hp(2.5),
        color: theme.colors.text,
        fontWeight: "500",
    },
    loading: {
        alignItems: 'center',
        justifyContent: 'center',
        height: hp(5.8),
        width: hp(5.8)
    }
})

export default PostDetails